import React, { forwardRef, FunctionComponent, ReactNode } from 'react';
import { ReactElement } from 'react';

import Head from 'next/head';
import styled from 'styled-components';

import { Text } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';
import media from 'chime-ui/system/media';

type LayoutProps = {
  title?: string;
  children: ReactNode;
  header?: ReactElement;
  footer?: ReactElement;
  showTagline?: boolean;
  // Optional surface color for the whole screen (page + card). Defaults to the
  // existing white card; used by RFA screens for their warm surface. Additive —
  // callers that omit it are unaffected.
  background?: string;
  // Optional font-family for the whole screen (RFA screens use Chime Saans).
  // Additive — callers that omit it inherit the app default (Circular Pro).
  fontFamily?: string;
  // RFA desktop shell. When set, from tablet width up this header row sits above a
  // 560px white card on a #f7f7f7 page (Figma RFA desktop frames). Additive — callers
  // that omit it are unaffected, and nothing changes below tablet width.
  desktopHeader?: ReactElement;
};

const Layout: FunctionComponent<LayoutProps> = ({
  title = 'Chime - Pay Anyone',
  children,
  header,
  footer,
  background,
  fontFamily,
  desktopHeader,
}) => {
  const desktopShell = !!desktopHeader;
  return (
    <PageWrapper
      $background={background}
      $fontFamily={fontFamily}
      $desktopShell={desktopShell}
    >
      <Head>
        <title>{title}</title>
      </Head>
      {desktopHeader && (
        <DesktopHeaderSlot data-testid="layout-desktop-header">
          {desktopHeader}
        </DesktopHeaderSlot>
      )}
      <Card $background={background} $desktopShell={desktopShell}>
        {header && header}
        <CardContent $desktopShell={desktopShell}>
          <WebkitAnimationLayout>{children}</WebkitAnimationLayout>
        </CardContent>
      </Card>
      {footer ? (
        footer
      ) : (
        <Footer $desktopShell={desktopShell}>{<CopyrightText />}</Footer>
      )}
    </PageWrapper>
  );
};

export const CopyrightText = () => {
  const date = new Date();
  const year = date.getFullYear();

  return (
    <Text color={colors.darkGrey}>
      © 2013-{year} Chime. All rights reserved.
    </Text>
  );
};

export default Layout;

export const ContentWrapper = styled.div`
  position: relative;
  ${media.tablet`
    margin-bottom: 4.5rem;
  `};
`;

export const Footer = styled.footer<{ $desktopShell?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 5rem;

  * {
    text-align: center;
    font-size: 9px;
  }

  ${media.tablet`
    margin-top: 13rem;
  `}

  ${({ $desktopShell }) =>
    $desktopShell
      ? media.tablet`
          margin-top: 2.5rem;
        `
      : ''}
`;

export const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ fullHeight }) =>
    fullHeight
      ? media.mobileOnly`
          min-height: 75vh;
        `
      : ''}
`;

type ContentProps = React.HTMLProps<HTMLDivElement> & {
  fullHeight?: boolean;
};

export const Content: FunctionComponent<ContentProps> = forwardRef(
  (props, ref) => <ContentLayout ref={ref} fullHeight={true} {...props} />
);

export const SpacedContent = styled(Content)`
  justify-content: flex-start;
  margin-top: 2rem;
`;

export const PageWrapper = styled.main<{
  $background?: string;
  $fontFamily?: string;
  $desktopShell?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 37.5rem;
  margin: 0 auto;
  padding: 0 1.5rem;
  padding-bottom: 10vh;
  background: ${({ $background }) => $background || 'transparent'};
  /* When a full-bleed surface is set (RFA), fill the viewport so a short screen
     doesn't show a white band below the warm surface. Non-background pages are
     unaffected. dvh handles mobile browser chrome; vh is the fallback. */
  ${({ $background }) =>
    $background ? 'min-height: 100vh; min-height: 100dvh;' : ''}
  font-family: ${({ $fontFamily }) => $fontFamily || 'inherit'};

  ${media.tablet`
    padding-top: 65px;
    padding-bottom: 50px;
    padding-left: 0;
    padding-right: 0;
  `};

  ${({ $desktopShell }) =>
    $desktopShell
      ? media.tablet`
          max-width: none;
          justify-content: flex-start;
          padding: 0 0 3rem;
          background: ${RFA_DESKTOP_PAGE};
        `
      : ''}
`;

// RFA desktop shell values (Figma RFA desktop frames): the Nickel page, a white card
// with outline/secondary.
const RFA_DESKTOP_PAGE = '#f7f7f7';
const RFA_DESKTOP_CARD = '#ffffff';
const RFA_DESKTOP_CARD_OUTLINE = 'rgba(0, 0, 0, 0.04)';

const DesktopHeaderSlot = styled.div`
  display: none;

  ${media.tablet`
    display: block;
  `}
`;

const Card = styled.div<{ $background?: string; $desktopShell?: boolean }>`
  background: ${({ $background, theme }) => $background || theme.colors.white};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  ${media.tablet`
    min-height: 23rem;
    border-radius: 24px;
    box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.08), 0 24px 32px 0 rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
  `};

  ${({ $desktopShell }) =>
    $desktopShell
      ? media.tablet`
          width: 560px;
          margin: 2.5rem auto 0;
          min-height: 0;
          padding: 1.5rem;
          background: ${RFA_DESKTOP_CARD};
          border: 0.5px solid ${RFA_DESKTOP_CARD_OUTLINE};
          border-radius: 2rem;
          box-shadow: none;
        `
      : ''}
`;

const CardContent = styled.div<{ $desktopShell?: boolean }>`
  ${media.tablet`
    padding: 0 8rem 0;
  `};

  ${({ $desktopShell }) =>
    $desktopShell
      ? media.tablet`
          padding: 0;
        `
      : ''}
`;

// Animation might cause the page to scroll
// and this fixes the scrolling issue (See: PFG-579)
const WebkitAnimationLayout = styled.div`
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000px;
`;
